import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Star, Clock, Bike, ChevronRight, Loader2, MapPin, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useApp } from '../context/AppContext';
import { catalogApi } from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import CityCarousel from '../components/CityCarousel';
import VendorQuickStart from '../components/VendorQuickStart';
import BrandHero from '../components/BrandHero';
import { publicSettings } from '../services/publicSettings';
import LogisticsQuickBook from '../components/LogisticsQuickBook';
import AnnouncementTicker from '../components/AnnouncementTicker';
import HomeMarketplaceStrip from '../components/HomeMarketplaceStrip';

// Categories that trigger the logistics quick-book modal instead of the
// standard vendor-list filter behavior.
const LOGISTICS_CATS = new Set(['package', 'courier', 'logistics']);

const Home = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // Initialise from URL ?q= and ?cat= so service-tile clicks deep-link correctly.
  const initialParams = new URLSearchParams(location.search);
  const [query, setQuery] = useState(initialParams.get('q') || '');
  const [activeCat, setActiveCat] = useState(initialParams.get('cat') || 'all');
  const [activeCity, setActiveCity] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [categories, setCategories] = useState([]);
  const [promos, setPromos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorHeading, setVendorHeading] = useState('');
  const { format, delivery, setDelivery, fulfillment, setFulfillment } = useApp();
  const [quickBook, setQuickBook] = useState({ open: false, category: 'package' });

  const openQuickBook = (cat) => setQuickBook({ open: true, category: cat });

  // Sync local filter state when location.search changes (e.g. service tile click
  // pushes /?cat=grocery while we're already on /).
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setQuery(p.get('q') || '');
    setActiveCat(p.get('cat') || 'all');
  }, [location.search]);

  useEffect(() => {
    (async () => {
      try {
        const [cats, prs] = await Promise.all([catalogApi.categories(), catalogApi.promos()]);
        setCategories(cats);
        setPromos(prs);
      } catch { /* noop */ }
    })();
    // Landing labels come from platform_settings so admins can customize
    // "What can we deliver for you today?" without a code deploy.
    publicSettings.get().then((s) => setVendorHeading(s?.landing_vendor_heading || '')).catch(() => {});
  }, []);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    catalogApi.vendors({ category: activeCat === 'all' ? undefined : activeCat })
      .then((data) => { if (!canceled) setVendors(data); })
      .catch(() => { if (!canceled) setVendors([]); })
      .finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
  }, [activeCat]);

  const distanceKm = (v) => {
    if (delivery.lat == null || delivery.lng == null || v.lat == null || v.lng == null) return null;
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(v.lat - delivery.lat);
    const dLng = toRad(v.lng - delivery.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(delivery.lat)) * Math.cos(toRad(v.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const filtered = useMemo(() => {
    let list = vendors;
    if (activeCity !== 'all') {
      list = list.filter((v) => v.city === activeCity);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((v) =>
        v.name.toLowerCase().includes(q) ||
        (v.cuisine || '').toLowerCase().includes(q) ||
        (v.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list.map((v) => ({ ...v, _distance_km: distanceKm(v) }));
  }, [query, activeCity, vendors, delivery.lat, delivery.lng]);

  const cities = useMemo(() => {
    const map = new Map();
    vendors.forEach((v) => {
      const name = v.city;
      if (!name) return;
      const cur = map.get(name) || { name, country: v.country || '', count: 0 };
      cur.count += 1;
      map.set(name, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [vendors]);

  // Sort by proximity when delivery coords are known; hide >25km unless toggled
  const orderedFiltered = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const da = a._distance_km == null ? 9999 : a._distance_km;
      const db = b._distance_km == null ? 9999 : b._distance_km;
      return da - db;
    });
    return arr;
  }, [filtered]);

  const nearby = orderedFiltered.filter((v) => v._distance_km == null || v._distance_km <= 25);
  const far = orderedFiltered.filter((v) => v._distance_km != null && v._distance_km > 25);
  const visible = showAll ? orderedFiltered : nearby.slice(0, 5);

  return (
    <div className="min-h-screen bg-slate-50">
      <AnnouncementTicker />
      <Header onSearch={setQuery} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <BrandHero />

        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mt-8" data-testid="home-vendor-heading">
          {vendorHeading || t('home.hero_title', 'What can we deliver for you today?')}
        </h1>
        <p className="text-slate-500 mt-1">{t('home.hero_subtitle', 'Browse local restaurants, grocers, and more — in real time.')}</p>

        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-3 sm:p-4 shadow-sm" data-testid="home-delivery-address-bar">
          <div className="flex items-center gap-1.5 mb-2" data-testid="home-fulfillment-toggle">
            <button
              type="button"
              onClick={() => setFulfillment('delivery')}
              data-testid="home-fulfillment-delivery"
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                fulfillment === 'delivery'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              <MapPin className="h-3 w-3" /> {t('home.deliver_to_tab', 'Deliver to')}
            </button>
            <button
              type="button"
              onClick={() => setFulfillment('pickup')}
              data-testid="home-fulfillment-pickup"
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                fulfillment === 'pickup'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              <Store className="h-3 w-3" /> {t('home.pickup_tab', 'Pickup')}
            </button>
          </div>
          {fulfillment === 'pickup' ? (
            <div className="flex items-start gap-2 text-sm text-slate-600 px-1 py-2" data-testid="home-pickup-hint">
              <Store className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
              <span>{t('home.pickup_hint', "You'll collect at the store. Pick any nearby vendor below — no delivery address needed.")}</span>
            </div>
          ) : (
            <>
              <AddressAutocomplete
                value={delivery.address}
                onChange={(address, geo) => setDelivery({ address, lat: geo?.lat ?? null, lng: geo?.lng ?? null })}
                placeholder={t('home.address_placeholder', "Enter your delivery address — we'll find stores within 25 km")}
                icon={MapPin}
              />
              {delivery.lat == null && delivery.address && (
                <div className="text-[11px] text-amber-600 mt-1.5">{t('home.address_hint', 'Pick an address from the suggestions to enable nearby filtering.')}</div>
              )}
            </>
          )}
        </div>

        <HomeMarketplaceStrip />

        <VendorQuickStart />

        <div className="mt-4 flex flex-wrap gap-2" data-testid="hero-role-ctas">
          <Link to="/driver/apply" data-testid="hero-become-driver"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
            <Icons.Bike className="h-4 w-4" /> {t('home.become_driver', 'Become a driver')}
          </Link>
          <Link to="/vendor-onboarding" data-testid="hero-become-vendor"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-emerald-600 text-emerald-700 text-sm font-medium hover:bg-emerald-50">
            <Icons.Store className="h-4 w-4" /> {t('home.become_vendor', 'Become a vendor')}
          </Link>
          <Link to="/shop-anywhere" data-testid="hero-shop-anywhere"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-amber-500 text-amber-700 text-sm font-medium hover:bg-amber-50">
            <Icons.ShoppingBag className="h-4 w-4" /> {t('home.shop_anywhere_title', 'Shop anywhere')}
          </Link>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="home-logistics-shortcuts">
          <button
            type="button"
            onClick={() => openQuickBook('package')}
            data-testid="home-logistics-package"
            className="group text-left rounded-2xl p-4 bg-white border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all flex items-center gap-3"
          >
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
              <Icons.Package className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">Same-day Package</div>
              <div className="text-[11px] text-slate-500 truncate">Pickup + drop-off · driver-delivered</div>
            </div>
            <Icons.ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-sky-600 ml-auto shrink-0" />
          </button>
          <button
            type="button"
            onClick={() => openQuickBook('courier')}
            data-testid="home-logistics-courier"
            className="group text-left rounded-2xl p-4 bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all flex items-center gap-3"
          >
            <div className="h-11 w-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <Icons.Bike className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">Courier delivery</div>
              <div className="text-[11px] text-slate-500 truncate">Fast bike or scooter driver</div>
            </div>
            <Icons.ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 ml-auto shrink-0" />
          </button>
          <button
            type="button"
            onClick={() => openQuickBook('logistics')}
            data-testid="home-logistics-business"
            className="group text-left rounded-2xl p-4 bg-white border border-slate-200 hover:border-amber-300 hover:shadow-md transition-all flex items-center gap-3"
          >
            <div className="h-11 w-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <Icons.Truck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">Business Logistics</div>
              <div className="text-[11px] text-slate-500 truncate">Scheduled shipments · driver-run</div>
            </div>
            <Icons.ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600 ml-auto shrink-0" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((p) => (
            <div key={p.id} className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${p.bg} text-white shadow-md`}>
              <div className="bg-grain absolute inset-0 opacity-30 pointer-events-none" />
              <div className="relative">
                <Badge className="bg-white/15 hover:bg-white/15 text-white border-0 mb-3 backdrop-blur">Limited offer</Badge>
                <div className="text-lg font-semibold leading-snug">{p.title}</div>
                <div className="text-sm text-white/80 mt-1">{p.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Categories</h2>
          <button className="text-sm text-emerald-700 hover:text-emerald-800 flex items-center gap-0.5">
            View all <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
          <button
            onClick={() => setActiveCat('all')}
            className={`flex flex-col items-center gap-2 p-3 rounded-xl bg-white border ${activeCat === 'all' ? 'border-emerald-600 ring-2 ring-emerald-100' : 'border-slate-200 hover:border-slate-300'} card-hover`}
          >
            <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center">
              <Icons.Sparkles className="h-5 w-5" />
            </div>
            <span className="text-xs font-medium text-slate-700">All</span>
          </button>
          {categories.map((c) => {
            const Icon = Icons[c.icon] || Icons.Store;
            const active = activeCat === c.id;
            const isLogistics = LOGISTICS_CATS.has(String(c.id).toLowerCase());
            return (
              <button
                key={c.id}
                onClick={() => (isLogistics ? openQuickBook(c.id) : setActiveCat(c.id))}
                data-testid={isLogistics ? `home-quickbook-${c.id}` : `home-category-${c.id}`}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl bg-white border ${active ? 'border-emerald-600 ring-2 ring-emerald-100' : 'border-slate-200 hover:border-slate-300'} card-hover`}
              >
                <div className={`h-10 w-10 rounded-full ${c.color} flex items-center justify-center`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-slate-700">{c.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {cities.length > 1 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <CityCarousel cities={cities} active={activeCity} onChange={setActiveCity} />
        </section>
      )}

      <section id="home-vendor-list" data-testid="home-vendor-list" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 pb-16 scroll-mt-20">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900">
            {activeCity !== 'all'
              ? `Stores in ${activeCity}`
              : (activeCat === 'all' ? t('home.popular_near_you', 'Popular near you') : (categories.find((c) => c.id === activeCat)?.name || 'Stores'))}
          </h2>
          <span className="text-sm text-slate-500">
            {showAll ? `${orderedFiltered.length}` : `${nearby.slice(0, 5).length} of ${nearby.length}`}
            {' '}{nearby.length === 1 ? 'store' : 'stores'}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto text-emerald-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
            <div className="text-slate-700 font-medium">No stores in this category yet.</div>
            <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
              We&apos;re actively onboarding merchants for{' '}
              <span className="font-medium">{categories.find((c) => c.id === activeCat)?.name || 'this category'}</span>.
              Browse what&apos;s already available or invite a merchant you know.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <Link
                to="/?cat=all"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                data-testid="browse-all-stores"
              >
                Browse all stores
              </Link>
              <Link
                to="/vendor-onboarding"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-emerald-600 text-emerald-700 text-sm font-medium hover:bg-emerald-50"
              >
                Invite a merchant
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((v) => {
              const farAway = v._distance_km != null && v._distance_km > 15;
              return (
              <Link key={v.id} to={`/vendor/${v.id}`} className="group">
                <Card className={`overflow-hidden border-slate-200 card-hover ${farAway ? 'opacity-60' : ''}`}>
                  <div className="relative h-44 w-full overflow-hidden bg-slate-100">
                    <img src={v.cover} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {v.promo && (
                      <Badge className="absolute top-3 left-3 bg-emerald-600 hover:bg-emerald-600 text-white border-0">{v.promo}</Badge>
                    )}
                    {v.has_gift_credit && (
                      <Badge
                        className="absolute top-3 left-3 bg-amber-500 hover:bg-amber-500 text-white border-0 shadow-md"
                        style={{ marginTop: v.promo ? 26 : 0 }}
                        data-testid={`gift-credit-badge-${v.id}`}
                      >
                        <span className="mr-1">🎁</span> Store credit available
                      </Badge>
                    )}
                    {farAway && (
                      <Badge className="absolute top-3 right-3 bg-amber-500 hover:bg-amber-500 text-white border-0">Out of area</Badge>
                    )}
                    <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-full px-2.5 py-1 text-xs font-medium text-slate-800 flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-slate-500" /> {v.deliveryTime}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 leading-tight">{v.name}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{v.cuisine} • {v._distance_km != null ? `${v._distance_km.toFixed(1)} km` : v.distance}</p>
                      </div>
                      <div className="shrink-0 flex items-center gap-1 bg-slate-900 text-white rounded-md px-1.5 py-0.5 text-xs font-medium">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /> {v.rating}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Bike className="h-3.5 w-3.5" /> {format(v.deliveryFee, v.currency)}</span>
                      <span>•</span>
                      <span>{(v.reviews ?? 0).toLocaleString()} reviews</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );})}
          </div>
        )}
        {!showAll && nearby.length > 5 && (
          <div className="mt-5 flex justify-center">
            <button onClick={() => setShowAll(true)} data-testid="view-all-stores-btn"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
              View all {nearby.length} nearby stores
              {far.length > 0 && <span className="text-xs opacity-70 ml-1">(+{far.length} farther)</span>}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
        {showAll && (orderedFiltered.length > 5) && (
          <div className="mt-5 flex justify-center">
            <button onClick={() => setShowAll(false)} data-testid="show-less-btn"
              className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
              Show less
            </button>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/shop-anywhere" data-testid="cta-shop-anywhere"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white p-6 flex items-start gap-4 hover:shadow-xl hover:-translate-y-0.5 transition-all">
            <Icons.ShoppingBag className="h-8 w-8 shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-widest opacity-80">For customers</div>
              <h3 className="text-lg font-semibold mt-0.5">Shop anywhere</h3>
              <p className="text-sm opacity-90 mt-1">Paste any merchant URL, cart text, or screenshot — our AI orders + dispatches a driver to pick it up.</p>
            </div>
            <Icons.ChevronRight className="h-5 w-5 absolute right-4 top-1/2 -translate-y-1/2 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </Link>
          <Link to="/vendor-onboarding" data-testid="cta-become-vendor"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-6 flex items-start gap-4 hover:shadow-xl hover:-translate-y-0.5 transition-all">
            <Icons.Store className="h-8 w-8 shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-widest opacity-80">For merchants</div>
              <h3 className="text-lg font-semibold mt-0.5">Become a ShipitNow vendor</h3>
              <p className="text-sm opacity-90 mt-1">Start from an AI template or import your existing site. Live in 60 seconds.</p>
            </div>
            <Icons.ChevronRight className="h-5 w-5 absolute right-4 top-1/2 -translate-y-1/2 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </Link>
        </div>
      </section>

      <LogisticsQuickBook
        open={quickBook.open}
        initialCategory={quickBook.category}
        onClose={() => setQuickBook((s) => ({ ...s, open: false }))}
      />

      {/* Trust / mission footer */}
      <footer className="mt-10 border-t border-slate-200 bg-white" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-[12px] sm:text-sm text-slate-600 leading-snug" data-testid="footer-mission-statement">
            ShipItNow uses its registered and approved drivers to deliver food, same-day delivery, courier services, and business logistics in Canada and Ibadan.
          </p>
          <p className="text-[11px] text-slate-400 mt-2">© {new Date().getFullYear()} ShipitNow · Ottawa &amp; Ibadan</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
